import React from "react";
import { useNavigate } from "react-router-dom";
import { Ornament } from "./Ornament";
import { Instagram, Phone, Mail } from "lucide-react";
import { BRAND } from "../data/content";

export default function About() {
  const navigate = useNavigate();
  return (
    <section
      id="about"
      className="relative section-y bg-[var(--lu-ivory)]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
        {/* Photo */}
        <div className="lg:col-span-5">
          <div className="tile overflow-hidden bg-[var(--lu-cream)] border border-[var(--lu-gold-dark)]/25">
            <img
              src="/generated/mata-portrait.png"
              alt="Mata Canto — Founder & Creative Director, Lumière Entertainment"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
          <div className="mt-6 text-center">
            <p className="font-serif italic text-[22px] text-[var(--lu-black)]">Stamatina Kanda</p>
            <p className="font-micro text-[10px] text-[var(--lu-gold-dark)] mt-1">
              Founder · Creative Director · Singer · Live Performer
            </p>
          </div>
        </div>

        {/* Copy */}
        <div className="lg:col-span-7">
          <p className="font-micro text-[10px] text-[var(--lu-gold-dark)]">
            About the Founder
          </p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--lu-black)] leading-[1.05]">
            From the stage to the world&apos;s most{" "}
            <span className="italic text-[var(--lu-gold-dark)]">
              unforgettable experiences
            </span>
            .
          </h2>
          <Ornament className="my-8 justify-start" />

          <div className="space-y-5 text-[16px] leading-[1.85] text-black-80 font-body">
            <p className="font-serif text-[20px] italic text-[var(--lu-black)]">
              With a background in live performance, music and creative direction,
              I bring together storytelling, entertainment and event design to
              create experiences that feel cinematic, emotional and deeply
              memorable.
            </p>
            <p>
              For years, I have worked as a singer, performer and entertainment
              producer, learning how audiences connect with emotion, rhythm and
              spectacle. Today, I translate that artistic vision into luxury
              events, brand activations and destination celebrations where every
              detail serves a story.
            </p>
            <p>
              At Lumière, every concept is created with one goal:{" "}
              <em className="text-[var(--lu-black)]">
                to transform an event into an experience people remember long
                after the night ends.
              </em>
            </p>
          </div>

          <div className="mt-10 p-6 border border-[var(--lu-gold-dark)]/30 bg-[var(--lu-cream)]">
            <p className="font-micro text-[10px] text-[var(--lu-gold-dark)] mb-3">
              In one line
            </p>
            <p className="font-serif italic text-[18px] leading-[1.55] text-[var(--lu-black)]">
              Lumière was founded by creative director, singer and live
              performer Stamatina Kanta. With a background in music,
              entertainment and event production, she combines artistic vision
              with strategic experience to create luxury events, destination
              celebrations and immersive brand experiences. Every project begins
              with a story.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 items-center">
            <span className="font-micro text-[10px] text-[var(--lu-gold-dark)]">
              Contact
            </span>
            <a
              href={`tel:+30${BRAND.phone.replace(/\s/g, "")}`}
              className="btn-gold-outline text-[10px] px-4 py-2 flex items-center gap-2"
            >
              <Phone size={12} /> {BRAND.phone}
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              className="btn-gold-outline text-[10px] px-4 py-2 flex items-center gap-2"
            >
              <Mail size={12} /> {BRAND.email}
            </a>
            <a
              href={`https://instagram.com/${BRAND.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold-outline text-[10px] px-4 py-2 flex items-center gap-2"
            >
              <Instagram size={12} /> @{BRAND.instagram}
            </a>
            <button
              onClick={() => navigate("/contact")}
              className="btn-gold text-[10px] px-4 py-2"
            >Request a Discovery Call</button>
          </div>
        </div>
      </div>

      {/* Editorial poster row */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-24 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="tile bg-[var(--lu-cream)] border border-[var(--lu-gold-dark)]/25 flex items-center justify-center p-2">
          <img
            src="/generated/mata-poster-1.png"
            alt="Lumière — We Design Experiences that Stay With You. Luxury Events & Design."
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
        <div className="tile bg-[var(--lu-cream)] border border-[var(--lu-gold-dark)]/25 flex items-center justify-center p-2">
          <img
            src="/generated/mata-magazine.png"
            alt="Lumière by Mata Canto — Where Vision Becomes Experience. Event Design · Entertainment · Luxury Concepts."
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
      </div>

      {/* OUR VISION — manifesto block */}
      <div className="max-w-4xl mx-auto px-6 mt-32 text-center" data-testid="about-vision">
        <p className="font-micro text-[10px] text-[var(--lu-gold-dark)]">Our Vision</p>
        <Ornament className="my-8" />
        <div className="space-y-3 font-serif italic text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.25] text-[var(--lu-black)]">
          <p>Where art meets hospitality.</p>
          <p>Where entertainment becomes emotion.</p>
          <p className="text-[var(--lu-gold-dark)]">
            Where every event becomes a signature experience.
          </p>
        </div>
        <p className="mt-10 text-[16px] leading-[1.9] text-black-70 font-light max-w-2xl mx-auto">
          Lumière exists to transform celebrations, brand activations and
          destination events into unforgettable stories through creativity,
          music, design and exceptional live entertainment.
        </p>
      </div>

      {/* Signature quote 1 */}
      <div className="max-w-4xl mx-auto px-6 mt-24 text-center" data-testid="about-quote-1">
        <p className="font-serif italic text-[28px] sm:text-[38px] lg:text-[46px] leading-[1.25] text-[var(--lu-gold-dark)]">
          &ldquo;Originality, high aesthetic event style with a sense of luxurism.&rdquo;
        </p>
      </div>

      {/* OUR PASSION — editorial block */}
      <div className="max-w-4xl mx-auto px-6 mt-24" data-testid="about-passion">
        <p className="font-micro text-[10px] text-[var(--lu-gold-dark)]">Our Passion</p>
        <h3 className="mt-6 font-serif text-[38px] sm:text-[48px] text-[var(--lu-black)] italic leading-[1.1]">
          Our passion
        </h3>
        <div className="deco-line-solid my-8 max-w-[220px] bg-[var(--lu-gold-dark)]/60" />
        <div className="space-y-5 text-[16px] leading-[1.85] text-black-80 font-body">
          <p>
            Mixing creativity and inspiration with the power of simplicity, high
            aesthetic, artistry and a sense of luxurism to produce an event
            concept is our passion.
          </p>
          <p className="font-serif italic text-[20px] text-[var(--lu-black)]">
            Creation is the basis of human identity. We create stories to
            explain the world around us and feel special to interact with life
            through a prism of exploration.
          </p>
          <p>
            We are a team dedicated to creating an event experience hard to
            distinguish where real life stops and dream begins. With flair,
            style and elegance we take your guests on a journey that goes beyond
            imagination.
          </p>
        </div>
      </div>

      {/* Signature quote 2 */}
      <div className="max-w-4xl mx-auto px-6 mt-24 text-center" data-testid="about-quote-2">
        <p className="font-serif italic text-[28px] sm:text-[38px] lg:text-[46px] leading-[1.25] text-[var(--lu-black)]">
          &ldquo;Themed event creations through a blending of ambience, scents,
          breathtaking views, aesthetic imagery, beautiful sounds, tastes and
          flavors.&rdquo;
        </p>
      </div>

      {/* ABOUT MATA — long-form personal editorial */}
      <div className="max-w-4xl mx-auto px-6 mt-24" data-testid="about-mata">
        <p className="font-micro text-[10px] text-[var(--lu-gold-dark)]">About Mata</p>
        <h3 className="mt-6 font-serif text-[38px] sm:text-[48px] lg:text-[58px] text-[var(--lu-black)] leading-[1.05]">
          About <span className="italic text-[var(--lu-gold-dark)]">Mata</span>
        </h3>
        <div className="deco-line-solid my-8 max-w-[220px] bg-[var(--lu-gold-dark)]/60" />
        <div className="space-y-5 text-[16px] leading-[1.85] text-black-80 font-body">
          <p>
            My vision is always the continuous development. Personally, I
            consider it&apos;s very important in every industry and at every
            level, to take advantage of the new challenges that arise and follow
            the new trends, as through them we are given the opportunity to
            progress and also to gain new experiences. In two words, I am in
            favor of the view that new challenges always &ldquo;give
            birth&rdquo; to new ideas. So the next step, in every phase of our
            journey, is continuous development, visioning and artistic
            inspiration.
          </p>
          <p className="font-serif italic text-[19px] text-[var(--lu-black)]">
            Inspiration for me is the first step in the pyramid of art. Moments
            from the past, images from nature itself which with its diversity
            sometimes combines harmoniously and sometimes more intensely, are
            things that inspire me and make me creative. Many times the
            memories of trips both in Greece and abroad have taken my
            imagination to a more artistic dimension. Like books, or even a
            scene from a movie, they are a source of inspiration for me. A song
            perhaps, or a musical tune, has the potential to seduce me just as
            much.
          </p>
          <p>
            I believe, first as a person and then as an entrepreneur in this
            area, that our emotions themselves have colors and intensities of
            an artistic nature. From there, by combining uniqueness and
            professionalism, we can implement a special event.
          </p>
        </div>
      </div>

      {/* Signature quote 3 */}
      <div className="max-w-4xl mx-auto px-6 mt-24 mb-4 text-center" data-testid="about-quote-3">
        <p className="font-serif italic text-[28px] sm:text-[38px] lg:text-[46px] leading-[1.25] text-[var(--lu-gold-dark)]">
          &ldquo;Creative insights, excellent execution.&rdquo;
        </p>
      </div>
    </section>
  );
}
